import { definedMap } from "@glideapps/ts-necessities";

import { TableName } from "common/database-strings";
import {
    FilterReferenceSpecification,
    TextTemplateSpecification,
    IfThenElseSpecification,
    SyntheticColumnKind,
    LookupSpecification,
    SingleValueSpecification,
    MathSpecification,
    RollupSpecification,
    ValueFormatSpecification,
    GeoDistanceSpecification,
    JoinStringsSpecification,
    SplitStringSpecification,
    GenerateImageSpecification,
    UserAPIFetchColumnSpecification,
    ConstructURLSpecification,
    YesCodeSpecification,
} from "common/generator/formulas/formula-specifications";
import { TableColumn, PrimitiveGlideTypeKind, TypeSchema } from "common/generator/description";
import { GridSelection } from "@glideapps/glide-data-grid";
import { RecomputeTiming, TemporaryComputedColumn } from "common/components/types";

export enum DataColumnKind {
    Regular = "regular",
    UserSpecific = "user-specific",
    RowID = "row-id",
}

export type EditedColumnKind = SyntheticColumnKind | DataColumnKind;

export type TemporaryColumnData =
    | ReferenceColumnData
    | TextTemplateColumnData
    | IfThenElseColumnData
    | LookupColumnData
    | SingleValueColumnData
    | MathColumnData
    | RollupColumnData
    | JoinStringsColumnData
    | SplitStringColumnData
    | GeoDistanceColumnData
    | GenerateImageColumnData
    | UserAPIFetchColumnData
    | ConstructURLColumnData
    | YesCodeColumnData
    | RegularColumnData
    | UserSpecificColumnData
    | RowIDColumnData;

export function isTemporaryDataColumnData(t: TemporaryColumnData): t is RegularColumnData | UserSpecificColumnData {
    return t.kind === DataColumnKind.Regular || t.kind === DataColumnKind.UserSpecific;
}

interface SyntheticColumnData<TKind, TSpec> {
    readonly kind: TKind;
    readonly spec: Partial<TSpec>;
}

export type ReferenceColumnData = SyntheticColumnData<
    SyntheticColumnKind.FilterReference,
    FilterReferenceSpecification
>;
export type TextTemplateColumnData = SyntheticColumnData<SyntheticColumnKind.TextTemplate, TextTemplateSpecification>;
export type IfThenElseColumnData = SyntheticColumnData<SyntheticColumnKind.IfThenElse, IfThenElseSpecification>;
export type LookupColumnData = SyntheticColumnData<SyntheticColumnKind.Lookup, LookupSpecification>;
export type SingleValueColumnData = SyntheticColumnData<SyntheticColumnKind.SingleValue, SingleValueSpecification>;
export type MathColumnData = SyntheticColumnData<SyntheticColumnKind.Math, MathSpecification>;
export type RollupColumnData = SyntheticColumnData<SyntheticColumnKind.Rollup, RollupSpecification>;
export type JoinStringsColumnData = SyntheticColumnData<SyntheticColumnKind.JoinStrings, JoinStringsSpecification>;
export type SplitStringColumnData = SyntheticColumnData<SyntheticColumnKind.SplitString, SplitStringSpecification>;
export type GeoDistanceColumnData = SyntheticColumnData<SyntheticColumnKind.GeoDistance, GeoDistanceSpecification>;
export type GenerateImageColumnData = SyntheticColumnData<
    SyntheticColumnKind.GenerateImage,
    GenerateImageSpecification
>;
export type UserAPIFetchColumnData = SyntheticColumnData<
    SyntheticColumnKind.UserAPIFetch,
    UserAPIFetchColumnSpecification
>;
export type ConstructURLColumnData = SyntheticColumnData<SyntheticColumnKind.ConstructURL, ConstructURLSpecification>;
export type YesCodeColumnData = SyntheticColumnData<SyntheticColumnKind.YesCode, YesCodeSpecification>;

export interface RegularColumnData {
    readonly kind: DataColumnKind.Regular;
    readonly spec: { readonly typeKind: PrimitiveGlideTypeKind };
}

export interface UserSpecificColumnData {
    readonly kind: DataColumnKind.UserSpecific;
    readonly spec: { readonly typeKind: PrimitiveGlideTypeKind };
}

export interface RowIDColumnData {
    readonly kind: DataColumnKind.RowID;
    readonly spec: {};
}

export interface EditedColumn {
    readonly name: string;
    readonly displayName: string;
    readonly temporaryColumn: TemporaryColumnData;
    readonly originalColumn: TableColumn | undefined;
    readonly originalSchema: TypeSchema | undefined;
    readonly temporaryColumnFormat: ValueFormatSpecification | undefined;
    readonly computationModelInfo: TemporaryComputedColumn | undefined;
    readonly newColumn: TableColumn | undefined;
}

export interface NCMPerformanceAnalysisPayload {
    shouldTrigger: boolean;
    type: "strict" | "deferred";
}

export interface TablesState {
    readonly selectedTable: TableName | undefined;
    readonly editedColumn: EditedColumn | undefined;
    readonly showAllData: boolean;
    readonly isValid: boolean;
    readonly columnFilter: string;
    readonly selectedRows: readonly number[];
    readonly selectedColumn: string | undefined;
    readonly gridSelection: GridSelection | undefined;
    readonly triggerNCMPermanceAnalysis: NCMPerformanceAnalysisPayload;
    readonly ncmTimingStats: RecomputeTiming | undefined;
}

export function updateTemporaryColumnData(
    state: TemporaryColumnData,
    update: Partial<TemporaryColumnData>
): TemporaryColumnData {
    return {
        ...state,
        ...update,
        spec: {
            ...state.spec,
            ...update.spec,
        },
    } as TemporaryColumnData;
}

// NOTE: This function doesn't update the column format in `newColumn`.
// That's the caller's responsibility.
export function updateEditedColumn(
    state: EditedColumn,
    name: string | undefined,
    displayName: string | undefined,
    columnUpdate: Partial<TemporaryColumnData>,
    formatUpdate: Partial<ValueFormatSpecification> | undefined,
    newColumn: TableColumn | undefined
): EditedColumn {
    return {
        ...state,
        name: name ?? state.name,
        displayName: displayName ?? state.displayName,
        temporaryColumn: updateTemporaryColumnData(state.temporaryColumn, columnUpdate),
        temporaryColumnFormat:
            formatUpdate === undefined
                ? undefined
                : definedMap(
                      state.temporaryColumnFormat,
                      tcf => ({ ...tcf, ...formatUpdate } as ValueFormatSpecification)
                  ),
        newColumn: newColumn ?? state.newColumn,
    };
}
